import React, { useEffect, useState } from "react";
import { apiAdmin } from "../../lib/api";
import { Plus, Trash2, Edit3, X, Tag, Percent, Calendar, Power } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", code: "", percentage: 10, expires_at: "", active: true };

const Discounts = () => {
  const [items, setItems] = useState([]);
  const [globalSettings, setGlobalSettings] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [savingGlobal, setSavingGlobal] = useState(false);

  const load = () => apiAdmin.get("/discount-codes").then((r) => setItems(r.data));
  const loadSettings = () => apiAdmin.get("/settings").then((r) => setGlobalSettings(r.data));

  useEffect(() => { load(); loadSettings(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setShowForm(true); };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name,
      code: c.code,
      percentage: c.percentage,
      // Convert ISO datetime to <input type="datetime-local"> format YYYY-MM-DDTHH:mm
      expires_at: c.expires_at ? c.expires_at.slice(0, 16) : "",
      active: c.active,
    });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.name || !form.code) { toast.error("Nombre y código son requeridos"); return; }
    const pct = Number(form.percentage);
    if (isNaN(pct) || pct <= 0 || pct > 100) { toast.error("Porcentaje debe estar entre 1 y 100"); return; }
    const payload = {
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      percentage: pct,
      active: !!form.active,
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
    };
    try {
      if (editing) await apiAdmin.put(`/discount-codes/${editing.id}`, payload);
      else await apiAdmin.post("/discount-codes", payload);
      toast.success("Guardado");
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const toggleActive = async (c) => {
    try {
      await apiAdmin.put(`/discount-codes/${c.id}`, { active: !c.active });
      toast.success(c.active ? "Cupón desactivado" : "Cupón activado");
      load();
    } catch { toast.error("Error"); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este cupón? Esta acción no se puede deshacer.")) return;
    await apiAdmin.delete(`/discount-codes/${id}`);
    toast.success("Cupón eliminado");
    load();
  };

  const saveGlobal = async () => {
    if (!globalSettings) return;
    setSavingGlobal(true);
    try {
      await apiAdmin.put("/settings", globalSettings);
      toast.success("Descuento global guardado");
    } catch { toast.error("Error al guardar"); }
    finally { setSavingGlobal(false); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "Sin vencimiento";
    try {
      return new Date(iso).toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" });
    } catch { return iso; }
  };

  const isExpired = (iso) => {
    if (!iso) return false;
    try { return new Date(iso) < new Date(); } catch { return false; }
  };

  return (
    <div data-testid="admin-discounts">
      <div className="mb-10">
        <span className="text-xs uppercase tracking-[0.25em] font-bold text-yolk">─ Descuentos ─</span>
        <h1 className="mt-2 font-heading text-4xl font-bold text-brand-ink">Descuentos y cupones</h1>
        <p className="text-gray-600 mt-2">
          Configura el descuento global de la tienda y crea cupones para tus clientes. Los descuentos son <strong>acumulables</strong>: si el cliente tiene un cupón y la tienda está en descuento, se suman los porcentajes.
        </p>
      </div>

      {/* GLOBAL DISCOUNT */}
      <div className="bg-white border-2 border-black p-6 md:p-8 mb-8" data-testid="global-discount-card">
        <div className="flex items-center gap-2 mb-2">
          <Percent className="h-5 w-5 text-mint-deep" />
          <h2 className="font-heading text-xl font-bold text-brand-ink">Descuento global de tienda</h2>
        </div>
        <p className="text-sm text-gray-600 mb-5">
          Al activarlo, todos los pedidos en el carrito mostrarán este descuento aplicado al subtotal. Los precios de los productos se ven normales — el descuento se refleja en la suma del carrito.
        </p>
        {globalSettings ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">% Descuento global</label>
              <input
                type="number" min="0" max="100"
                value={globalSettings.global_discount_percent ?? 0}
                onChange={(e) => setGlobalSettings({ ...globalSettings, global_discount_percent: Number(e.target.value) })}
                data-testid="discounts-global-pct"
                className="w-full border-2 border-black px-4 py-3 text-sm"
              />
            </div>
            <label className="flex items-center gap-3 pb-3">
              <input
                type="checkbox"
                checked={!!globalSettings.global_discount_active}
                onChange={(e) => setGlobalSettings({ ...globalSettings, global_discount_active: e.target.checked })}
                data-testid="discounts-global-active"
                className="h-5 w-5"
              />
              <span className="text-sm font-bold">{globalSettings.global_discount_active ? "ACTIVO ✓" : "Inactivo"}</span>
            </label>
            <button onClick={saveGlobal} disabled={savingGlobal} data-testid="discounts-global-save" className="btn-chunky inline-flex items-center justify-center gap-2 bg-mint-deep text-white px-6 py-3 text-sm">
              {savingGlobal ? "Guardando..." : "GUARDAR GLOBAL"}
            </button>
          </div>
        ) : <p className="text-gray-400">Cargando...</p>}
      </div>

      {/* COUPONS */}
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-yolk" />
            <h2 className="font-heading text-2xl font-bold text-brand-ink">Cupones de descuento</h2>
          </div>
          <p className="text-sm text-gray-600 mt-1">Crea cupones con código, % y fecha de vencimiento. El cliente lo aplica en el carrito.</p>
        </div>
        <button onClick={openNew} data-testid="discount-new-btn" className="btn-chunky inline-flex items-center gap-2 bg-mint-deep text-white px-5 py-3 text-sm">
          <Plus className="h-4 w-4" /> Nuevo cupón
        </button>
      </div>

      <div className="bg-white border-2 border-black overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-mint-deep text-left text-xs uppercase tracking-wider text-white border-b-2 border-black">
            <tr>
              <th className="px-6 py-4 font-bold">Código</th>
              <th className="px-6 py-4 font-bold">Nombre</th>
              <th className="px-6 py-4 font-bold">%</th>
              <th className="px-6 py-4 font-bold">Vence</th>
              <th className="px-6 py-4 font-bold">Estado</th>
              <th className="px-6 py-4 font-bold">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y-2 divide-black/10">
            {items.length === 0 ? (
              <tr><td colSpan={6} className="px-6 py-12 text-center text-gray-400">Aún no hay cupones</td></tr>
            ) : items.map((c) => {
              const expired = isExpired(c.expires_at);
              return (
                <tr key={c.id} data-testid={`discount-row-${c.id}`} className="hover:bg-mint-light/40">
                  <td className="px-6 py-4 font-mono font-bold text-brand-ink">{c.code}</td>
                  <td className="px-6 py-4 text-gray-700">{c.name}</td>
                  <td className="px-6 py-4 font-bold text-mint-deep">{c.percentage}%</td>
                  <td className="px-6 py-4 text-xs text-gray-600">
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(c.expires_at)}</span>
                  </td>
                  <td className="px-6 py-4">
                    {expired ? (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-red-100 text-red-700 border border-red-300 font-bold">Vencido</span>
                    ) : c.active ? (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-mint-light text-mint-deep border border-mint-deep font-bold">Activo</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-gray-100 text-gray-500 border border-gray-300 font-bold">Inactivo</span>
                    )}
                  </td>
                  <td className="px-6 py-4 flex items-center gap-2">
                    <button onClick={() => toggleActive(c)} data-testid={`discount-toggle-${c.id}`} title={c.active ? "Desactivar" : "Activar"} className="p-2 hover:bg-yolk-light">
                      <Power className={`h-4 w-4 ${c.active ? "text-mint-deep" : "text-gray-400"}`} />
                    </button>
                    <button onClick={() => openEdit(c)} data-testid={`discount-edit-${c.id}`} className="p-2 hover:bg-yolk-light"><Edit3 className="h-4 w-4" /></button>
                    <button onClick={() => remove(c.id)} data-testid={`discount-delete-${c.id}`} className="p-2 hover:bg-red-50"><Trash2 className="h-4 w-4 text-red-500" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* FORM MODAL */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={() => setShowForm(false)}>
          <div className="bg-white border-2 border-black w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b-2 border-black sticky top-0 bg-white">
              <h2 className="font-heading text-xl font-bold">{editing ? "Editar" : "Nuevo"} cupón</h2>
              <button onClick={() => setShowForm(false)} data-testid="discount-form-close" className="p-2 hover:bg-yolk-light"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">Nombre interno</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej: Promo Verano" data-testid="discount-form-name" className="w-full border-2 border-black px-4 py-3 text-sm" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">Código (cliente lo ingresa)</label>
                <input type="text" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="VERANO20" data-testid="discount-form-code" className="w-full border-2 border-black px-4 py-3 text-sm font-mono uppercase" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">Porcentaje de descuento</label>
                <input type="number" min="1" max="100" value={form.percentage} onChange={(e) => setForm({ ...form, percentage: Number(e.target.value) })} data-testid="discount-form-pct" className="w-full border-2 border-black px-4 py-3 text-sm" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">Vence (opcional)</label>
                <input
                  type="datetime-local"
                  value={form.expires_at}
                  onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                  data-testid="discount-form-expires"
                  className="w-full border-2 border-black px-4 py-3 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Si lo dejas vacío, el cupón no vence.</p>
              </div>
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="discount-form-active" className="h-5 w-5" />
                <span className="text-sm">Activo</span>
              </label>
              <div className="flex gap-3 pt-4">
                <button onClick={() => setShowForm(false)} className="btn-chunky flex-1 bg-white text-black py-3 text-sm">Cancelar</button>
                <button onClick={submit} data-testid="discount-form-submit" className="btn-chunky flex-1 bg-mint-deep text-white py-3 text-sm">Guardar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Discounts;
